import React from "react";
import { Visibility } from "mapbox-gl";
import { Layer } from "react-map-gl";
import {
  customMapConfig,
  textColor,
  textFont,
  textHaloColor,
  textOpacity,
  transparent,
} from "common-client/utils/mapLayers";

export const getLinesLayer = ({
  id,
  sourceLayer,
  visibility,
  isObject,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
  isObject: boolean;
}) => {
  return (
    <Layer
      id={`${id}-lines`}
      type="line"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
        "line-cap": "round",
        "line-join": "round",
      }}
      paint={{
        "line-width": [
          "case",
          ["any", customMapConfig.clickFilter, customMapConfig.hoverFilter],
          customMapConfig.getLineInteractionWidth({ minWidth: 4 }),
          customMapConfig.getLineWidth(
            isObject ? { minWidth: 4 } : { defaultWidth: 2 },
          ),
        ],
        "line-color": customMapConfig.getFeatureColorForState({
          defaultColor: customMapConfig.lineColor,
        }),
        "line-opacity": customMapConfig.lineOpacity,
      }}
      filter={customMapConfig.includeAllLineStringFilter}
    />
  );
};

export const getFillPointsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-fill-points`}
      type="symbol"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
        "icon-image": customMapConfig.iconImageFilled,
        "icon-allow-overlap": true,
        "icon-size": customMapConfig.iconSize,
      }}
      paint={{
        "icon-color": [
          "case",
          ["any", customMapConfig.hoverFilter, customMapConfig.clickFilter],
          transparent,
          customMapConfig.fillColor,
        ],
        "icon-opacity": customMapConfig.fillOpacity,
      }}
      filter={customMapConfig.includeAllPointFilter}
    />
  );
};

export const getOutlinePointsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-outline-points`}
      type="symbol"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
        "icon-image": customMapConfig.iconImage,
        "icon-allow-overlap": true,
        "icon-size": customMapConfig.iconSize,
      }}
      paint={{
        "icon-color": [
          "case",
          ["any", customMapConfig.hoverFilter, customMapConfig.clickFilter],
          transparent,
          customMapConfig.lineColor,
        ],
        "icon-opacity": customMapConfig.lineOpacity,
      }}
      filter={customMapConfig.includeAllPointFilter}
    />
  );
};

export const getSelectedPointsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-fill-selected-points`}
      type="symbol"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
        "icon-image": customMapConfig.iconImageFilled,
        "icon-allow-overlap": true,
        "icon-size": customMapConfig.iconSize,
      }}
      paint={{
        "icon-color": [
          "case",
          ["any", customMapConfig.hoverFilter, customMapConfig.clickFilter],
          customMapConfig.fillColor,
          transparent,
        ],
        "icon-opacity": customMapConfig.fillOpacity,
      }}
      filter={customMapConfig.includeAllPointFilter}
    />
  );
};

export const getOutlineSelectedPointsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-outline-selected-points`}
      type="symbol"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
        "icon-image": customMapConfig.iconImage,
        "icon-allow-overlap": true,
        "icon-size": customMapConfig.iconSize,
      }}
      paint={{
        "icon-color": customMapConfig.getFeatureColorForState({
          defaultColor: transparent,
        }),
        "icon-opacity": 1,
      }}
      filter={customMapConfig.includeAllPointFilter}
    />
  );
};

export const getFillColorPolygonsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-fill-color-polygons`}
      type="fill"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
      }}
      paint={{
        "fill-color": customMapConfig.fillColor,
        "fill-opacity": customMapConfig.fillOpacity,
      }}
      filter={customMapConfig.includeAllPolygonFilter}
    />
  );
};

export const getFillPatternPolygonsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-fill-pattern-polygons`}
      type="fill"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
      }}
      // we have to have two separate polygon fills because
      // we can't use fill-pattern and fill-color in the same layer
      // see: https://github.com/mapbox/mapbox-gl-js/issues/8514
      paint={{
        "fill-pattern": customMapConfig.fillPattern,
      }}
      filter={customMapConfig.includeAllPolygonFilter}
    />
  );
};

export const getOutlinePolygonsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-outline-polygons`}
      type="line"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
      }}
      paint={{
        "line-opacity": [
          "case",
          customMapConfig.clickFilter,
          0,
          customMapConfig.hoverFilter,
          0,
          customMapConfig.lineOpacity,
        ],
        "line-color": customMapConfig.lineColor,
        "line-width": customMapConfig.getLineWidth({ defaultWidth: 2 }),
      }}
      filter={customMapConfig.includeAllPolygonFilter}
    />
  );
};

export const getLabelLinesLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-label-lines`}
      type="symbol"
      source-layer={sourceLayer}
      layout={{
        "symbol-placement": "line-center",
        visibility,
        "text-field": customMapConfig.textField,
        "text-padding": 10,
        "text-size": [
          "interpolate",
          ["exponential", 1],
          ["zoom"],
          0,
          10,
          10,
          15,
          15,
          20,
        ],
        "text-font": textFont,
      }}
      paint={{
        "text-color": textColor,
        "text-halo-color": textHaloColor,
        "text-opacity": textOpacity,
        "text-halo-width": 1,
      }}
      filter={customMapConfig.includeAllLineStringFilter}
    />
  );
};

export const getLabelPolygonsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-label-polygons`}
      type="symbol"
      source-layer={sourceLayer}
      layout={{
        visibility,
        "text-field": customMapConfig.textField,
        "text-padding": 10,
        "text-size": [
          "interpolate",
          ["exponential", 1],
          ["zoom"],
          0,
          10,
          10,
          15,
          15,
          20,
        ],
        "text-font": textFont,
        "symbol-avoid-edges": false,
      }}
      paint={{
        "text-color": textColor,
        "text-halo-color": textHaloColor,
        "text-opacity": textOpacity,
        "text-halo-width": 1,
      }}
      filter={customMapConfig.includeAllPolygonFilter}
    />
  );
};

export const getOutlineClickPolygonsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-outline-click-polygons`}
      type="line"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
      }}
      paint={{
        "line-opacity": ["case", customMapConfig.clickFilter, 1, 0],
        "line-color": customMapConfig.getFeatureColorForState({
          defaultColor: transparent,
        }),
        "line-width": customMapConfig.getLineWidth({ minWidth: 2 }),
      }}
      filter={customMapConfig.includeAllPolygonFilter}
    />
  );
};

export const getOutlineHoverPolygonsLayer = ({
  id,
  sourceLayer,
  visibility,
}: {
  id: string;
  sourceLayer: string;
  visibility: Visibility;
}) => {
  return (
    <Layer
      id={`${id}-outline-hover-polygons`}
      type="line"
      source-layer={sourceLayer}
      beforeId="ground"
      layout={{
        visibility,
      }}
      paint={{
        "line-opacity": ["case", customMapConfig.hoverFilter, 1, 0],
        "line-color": customMapConfig.getFeatureColorForState({
          defaultColor: transparent,
        }),
        "line-width": customMapConfig.getLineWidth({ minWidth: 2 }),
      }}
      filter={customMapConfig.includeAllPolygonFilter}
    />
  );
};
